import { AUDIO_GROUPS, createGameAssets } from "../game-assets.js";

const MAX_DELAY_MS = { scratch: 250, cat: 500, mistake: 500, board: 1200, finish: 1500 };
const LIMITS = { scratch: 3, cat: 2, mistake: 1, board: 1, finish: 1 };

// Media preloading starts the browser's audio service asynchronously. Opening a
// cold AudioContext directly can otherwise block the game's first paint.
function prepareAudioDevice(url) {
  if (typeof globalThis.Audio !== "function") return null;
  const media = new Audio();
  media.preload = "auto";
  let finish;
  const ready = new Promise((resolve) => { finish = resolve; });
  const timer = setTimeout(() => finish(), 1200);
  media.addEventListener("loadeddata", () => { clearTimeout(timer); finish(); }, { once: true });
  media.addEventListener("error", () => { clearTimeout(timer); finish(); }, { once: true });
  media.src = url;
  media.load();
  return {
    ready,
    release() {
      clearTimeout(timer);
      media.pause();
      media.removeAttribute("src");
      media.load();
      finish();
    },
  };
}

/** Small recorded effects, decoded once and scheduled without blocking a move. */
export class GameAudio {
  constructor({
    assets = createGameAssets("/"),
    createContext = () => {
      const Audio = globalThis.AudioContext || globalThis.webkitAudioContext;
      return Audio ? new Audio({ latencyHint: "interactive" }) : null;
    },
    prepareDevice = () => prepareAudioDevice(assets.audio[AUDIO_GROUPS.board[0]]),
    fetchAudio = (url) => globalThis.fetch(url, { priority: "low" }),
    random = Math.random,
    visibility = globalThis.document,
    now = () => performance.now(),
  } = {}) {
    this.assets = assets;
    this.createContext = createContext;
    this.prepareDevice = prepareDevice;
    this.fetchAudio = fetchAudio;
    this.random = random;
    this.visibility = visibility;
    this.now = now;
    this.enabled = true;
    this.voices = new Set();
    this.kindVersions = new Map();
    this.generation = 0;
    this.lastScratch = -Infinity;
    this.previousVariant = -1;
  }
  preload() {
    if (this.filesReady) return this.filesReady;
    this.files = new Map();
    for (const name of Object.values(AUDIO_GROUPS).flat()) {
      const job = Promise.resolve().then(async () => {
        try {
          const response = await this.fetchAudio(this.assets.audio[name]);
          return response.ok ? await response.arrayBuffer() : null;
        } catch { return null; }
      });
      this.files.set(name, job);
    }
    this.filesReady = Promise.all(this.files.values());
    return this.filesReady;
  }
  loadBuffers(context) {
    this.preload();
    const jobs = new Map();
    for (const [name, file] of this.files) {
      jobs.set(name, file.then(async (bytes) => {
        if (!bytes) return null;
        try { return await context.decodeAudioData(bytes.slice(0)); }
        catch { return null; }
      }));
    }
    this.buffers = jobs;
    this.ready = Promise.all(jobs.values());
  }
  unlock() {
    if (!this.enabled || this.hostPaused || this.destroyed || this.visibility?.hidden) return null;
    try {
      if (!this.context || this.context.state === "closed") {
        if (!this.devicePrepared) {
          if (this.warming) return null;
          this.deviceWarmup = this.prepareDevice();
          if (this.deviceWarmup) {
            this.warming = Promise.resolve(this.deviceWarmup.ready)
              .catch(() => {})
              .then(() => {
                this.devicePrepared = true;
                this.warming = null;
                if (!this.destroyed) this.unlock();
                this.deviceWarmup?.release();
                this.deviceWarmup = null;
              });
            return null;
          }
          this.devicePrepared = true;
        }
        const context = this.createContext();
        if (!context) return null;
        this.context = context;
        this.master = context.createGain();
        this.master.gain.value = 0.9;
        this.limiter = context.createDynamicsCompressor();
        this.limiter.threshold.value = -6;
        this.limiter.knee.value = 6;
        this.limiter.ratio.value = 4;
        this.limiter.attack.value = 0.003;
        this.limiter.release.value = 0.1;
        this.master.connect(this.limiter).connect(context.destination);
        this.loadBuffers(context);
      }
      // Browsers may hold this promise until a user gesture permits sound.
      if (this.context.state === "suspended" || this.context.state === "interrupted") {
        this.resumeJob = this.context.resume();
        this.resumeJob?.catch(() => {});
      }
      return this.context;
    } catch {
      // An unavailable device or decoder must never block gameplay.
      return null;
    }
  }
  setEnabled(enabled) {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    if (!enabled) this.suspend();
  }
  setHostPaused(paused) {
    this.hostPaused = paused;
    if (paused) this.suspend();
  }
  dispose(voice) {
    if (!this.voices.delete(voice)) return;
    voice.source.onended = null;
    try { voice.source.stop(); } catch {}
    for (const node of [voice.source, voice.gain]) {
      try { node.disconnect(); } catch {}
    }
  }
  stopKind(kind) {
    this.kindVersions.set(kind, (this.kindVersions.get(kind) ?? 0) + 1);
    for (const voice of [...this.voices])
      if (voice.kind === kind) this.dispose(voice);
  }
  stopAll() {
    this.generation++;
    for (const voice of [...this.voices]) this.dispose(voice);
  }
  play(kind) {
    if (this.hostPaused) return false;
    const context = this.unlock();
    const warming = this.warming;
    if (!context && !warming) return false;
    let variant = 0;
    if (kind === "scratch") {
      variant = Math.floor(this.random() * AUDIO_GROUPS.scratch.length);
      if (variant === this.previousVariant) variant = (variant + 1) % AUDIO_GROUPS.scratch.length;
      this.previousVariant = variant;
    }
    const name = AUDIO_GROUPS[kind][variant];
    const request = {
      generation: this.generation,
      version: this.kindVersions.get(kind) ?? 0,
      requestedAt: this.now(),
    };
    if (context) void this.schedule(context, kind, name, request);
    else void warming.then(() => {
      const readyContext = this.unlock();
      if (readyContext) void this.schedule(readyContext, kind, name, request);
    });
    return true;
  }
  async schedule(context, kind, name, request) {
    try {
      const buffer = await this.buffers.get(name);
      if (!buffer) return false;
      if (context.state !== "running" && typeof context.startRendering !== "function")
        await this.resumeJob;
      if (!this.enabled || this.hostPaused || this.destroyed || this.visibility?.hidden || context.state === "closed" ||
        this.context !== context || this.generation !== request.generation ||
        (this.kindVersions.get(kind) ?? 0) !== request.version ||
        this.now() - request.requestedAt > MAX_DELAY_MS[kind]) return false;
      const active = [...this.voices].filter((voice) => voice.kind === kind);
      while (active.length >= LIMITS[kind]) this.dispose(active.shift());
      const source = context.createBufferSource();
      source.buffer = buffer;
      if (kind === "scratch") source.playbackRate.value = 0.97 + this.random() * 0.07;
      const gain = context.createGain();
      gain.gain.value = kind === "mistake" ? 0.8 : 1;
      source.connect(gain).connect(this.master);
      const voice = { source, gain, kind };
      source.onended = () => this.dispose(voice);
      this.voices.add(voice);
      const time = context.currentTime + 0.006;
      source.start(time);
      source.stop(time + buffer.duration / source.playbackRate.value);
      return true;
    } catch { return false; }
  }
  scratch() {
    if (!this.enabled || this.hostPaused || this.visibility?.hidden || this.now() - this.lastScratch < 60) return false;
    this.lastScratch = this.now();
    return this.play("scratch");
  }
  cat() {
    this.stopKind("scratch");
    this.stopKind("board");
    return this.play("cat");
  }
  mistake() {
    this.stopKind("scratch");
    this.stopKind("board");
    return this.play("mistake");
  }
  board() {
    this.stopAll();
    this.lastScratch = -Infinity;
    return this.play("board");
  }
  finish() {
    this.stopAll();
    return this.play("finish");
  }
  suspend() {
    this.stopAll();
    this.lastScratch = -Infinity;
    if (this.context?.state === "running") this.context.suspend()?.catch(() => {});
  }
  destroy() {
    this.suspend();
    this.destroyed = true;
    this.deviceWarmup?.release();
    this.deviceWarmup = null;
    this.context?.close?.()?.catch(() => {});
    this.context = null;
    this.buffers?.clear();
    this.files?.clear();
  }
}
